import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { KCard } from "@/components/KCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { changePassword, formatApiError } from "@/api/userAccount";

export default function PasswordChangePage() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const changePasswordMutation = useMutation({
    mutationFn: changePassword,
    onSuccess: (data) => {
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      toast.success(data?.message ?? "Password updated");
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  const onChangePassword = (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("New password and confirmation do not match");
      return;
    }
    changePasswordMutation.mutate({
      currentPassword,
      newPassword,
      confirmPassword,
    });
  };

  return (
    <DashboardLayout>
      <div className="dash-page max-w-3xl">
        <div className="dash-page-header">
          <div>
            <h1 className="dash-page-title">Change Password</h1>
            <p className="dash-page-subtitle">
              Update your account password. {"·"} {" "}
              <Link to="/profile-settings" className="text-primary hover:underline font-medium">
                Back to Profile Settings
              </Link>
            </p>
          </div>
        </div>

        <KCard className="p-5 sm:p-6 md:p-8 space-y-6">
          <form onSubmit={onChangePassword} className="w-full max-w-full space-y-4">
            <p className="text-sm text-muted-foreground">
              Password must be 8+ characters with uppercase, lowercase, number, and a special character (@$!%*?&amp;).
            </p>
            <div className="space-y-2">
              <Label htmlFor="currentPassword">Current password</Label>
              <Input
                id="currentPassword"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="newPassword">New password</Label>
              <Input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                required
                minLength={8}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm new password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
            </div>
            <div className="flex justify-end pt-2">
              <Button type="submit" disabled={changePasswordMutation.isPending}>
                {changePasswordMutation.isPending ? "Updating..." : "Update password"}
              </Button>
            </div>
          </form>
        </KCard>
      </div>
    </DashboardLayout>
  );
}
